import {
  Box,
  Flex,
  Icon,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { useState, useMemo } from 'react';
import { ChevronDownIcon, ChevronUpIcon, XIcon } from '@heroicons/react/solid';
import { KText } from '@/ui-library/components/text';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import {
  BulkOperationQueueProps,
  BulkOperationGroup,
} from './BulkOperationQueue.interface';
import { HiOutlineSparkles } from 'react-icons/hi';
import { Expand03 } from '@kula-ai/icons/solid/Expand03';
import { useBulkOperationsOptional } from './BulkOperationsContext';
import { getActionComponent } from './components';

// Operation group component
const OperationGroupSection = ({ group }: { group: BulkOperationGroup }) => {
  const [isGroupOpen, setIsGroupOpen] = useState(true);

  const handleToggle = () => {
    setIsGroupOpen(!isGroupOpen);
  };

  return (
    <Box>
      {/* Group Header - Clickable to toggle */}
      <Flex
        py="12px"
        px="20px"
        alignItems="center"
        gap="8px"
        backgroundColor="secondary-25"
        cursor="pointer"
        onClick={handleToggle}
        _hover={{ backgroundColor: 'secondary-50' }}
        transition="background-color 0.1s"
      >
        {group.icon && (
          <Icon as={group.icon} w="18px" h="18px" color="secondary-700" />
        )}
        <KText size="sm-semi-bold" color="secondary-900" flex={1}>
          {group.title}
        </KText>
        <Icon
          as={isGroupOpen ? ChevronUpIcon : ChevronDownIcon}
          w="16px"
          h="16px"
          color="secondary-700"
          transition="transform 0.1s"
        />
      </Flex>

      {/* Collapsible content */}
      <Box
        overflow="hidden"
        transition="all 0.15s ease-out"
        maxH={isGroupOpen ? '500px' : '0'}
        opacity={isGroupOpen ? 1 : 0}
      >
        {/* Item copy (e.g. "Added X candidates to flow") comes from item.name via getActivityItemName in transformers */}
        {/* Items - rendered using action-specific components */}
        <Box>
          {group.items.map((item) => {
            const ActionComponent = getActionComponent(group.id);
            return (
              <ActionComponent
                key={item.id}
                item={item}
                activityType={group.id}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export const BulkOperationQueue = ({
  config,
  leftOffset = '80px',
  bottomOffset = '0',
  width = '416px',
}: BulkOperationQueueProps) => {
  const [isMinimized, setIsMinimized] = useState(
    config.defaultMinimized ?? false
  );
  const bulkOps = useBulkOperationsOptional();

  const {
    groups,
    singleOperationTitle,
    showExpandButton = true,
    showCloseButton = true,
    onClose,
    onExpand,
    progress = 50,
  } = config;

  // Calculate in-progress operations count (only pending or in_progress)
  const inProgressCount = useMemo(() => {
    return groups.reduce((acc, group) => {
      const inProgressItems = group.items.filter(
        (item) => item.status === 'in_progress' || item.status === 'pending'
      );
      return acc + inProgressItems.length;
    }, 0);
  }, [groups]);

  // Calculate total operations count (for single operation check)
  const totalOperations = useMemo(() => {
    return groups.reduce((acc, group) => acc + group.items.length, 0);
  }, [groups]);

  // Check if it's a single operation (one group with one item)
  const isSingleOperation = groups.length === 1 && totalOperations === 1;

  // Get header title - only show in-progress count
  const headerTitle = useMemo(() => {
    if (isSingleOperation) {
      return singleOperationTitle || groups[0]?.title || 'Processing...';
    }
    // Only show in-progress operations count
    if (inProgressCount === 0) {
      return 'Activities completed';
    }
    return `${inProgressCount} Activities in-progress`;
  }, [isSingleOperation, singleOperationTitle, groups, inProgressCount]);

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleExpand = () => {
    if (onExpand) {
      onExpand();
    } else {
      // Fallback: navigate to recent activities using window.location
      // (useHistory may not work if component is outside Router context)
      window.location.href = '/notifications/recent-activities';
    }
  };

  const handleClose = async () => {
    // Only minimize to sidebar when activities are in-progress
    // When all activities are completed, hide the queue completely and mark as viewed
    if (inProgressCount > 0) {
      // Activities in-progress: minimize to sidebar so user can track progress
      if (bulkOps) {
        bulkOps.minimizeToSidebar();
      } else {
        onClose?.();
      }
    } else {
      // All activities completed: mark as viewed (entity_views call) and hide the queue
      if (bulkOps) {
        await bulkOps.markActivitiesAsViewed();
        bulkOps.hideQueue();
      } else {
        onClose?.();
      }
    }
  };

  // Rename to isOpen to match AiScoringBulkProgress pattern
  const isOpen = !isMinimized;

  return (
    <Box
      pos="fixed"
      bottom={bottomOffset}
      left={leftOffset}
      width={width}
      border="1px solid"
      borderColor="border-color"
      boxShadow="lg"
      borderRadius="8px 8px 0 0"
      zIndex={1000}
      data-test-id="bulk-operation-queue"
    >
      {/* Header - Always visible, clicking anywhere (except expand/close) minimizes */}
      <Flex
        h="50px"
        bg="ats-bg-secondary"
        borderRadius="8px 8px 0 0"
        alignItems="center"
        px="16px"
        gap="8px"
        overflow="hidden"
        cursor="pointer"
        onClick={handleMinimize}
      >
        {/* Icon for single operation with AI */}
        {isSingleOperation && groups[0]?.icon && (
          <Icon
            as={groups[0].icon}
            w="20px"
            h="20px"
            color="white"
            flexShrink={0}
          />
        )}

        {/* Title */}
        <Box flex={1} minW={0} overflow="hidden">
          <TruncatedTextWithTooltip
            text={headerTitle}
            color="base-white"
            size="sm-semi-bold"
            placement="top"
            toolTipVariant="default"
            maxW="100%"
          />
        </Box>

        {/* Progress bar */}
        <Box w="120px" flexShrink={0} display="flex" alignItems="center">
          <Slider
            value={progress}
            min={0}
            max={100}
            step={1}
            colorScheme="primary"
            size="sm"
            isReadOnly
            w="100%"
          >
            <SliderTrack bg="base-white" h="6px" borderRadius="6px">
              <SliderFilledTrack
                bg="linear-gradient(90deg, #8A90FF 0%, #B15FDA 100%)"
                borderRadius="6px"
              />
            </SliderTrack>
          </Slider>
        </Box>

        {/* Action buttons */}
        <Flex alignItems="center" gap="0" flexShrink={0}>
          {/* Minimize/Expand toggle */}
          <Tooltip
            label={isOpen ? 'Collapse' : 'Expand'}
            hasArrow={false}
            placement="top"
          >
            <IconButton
              aria-label={isOpen ? 'Minimize' : 'Expand'}
              icon={
                isOpen ? (
                  <ChevronDownIcon width={16} height={16} />
                ) : (
                  <ChevronUpIcon width={16} height={16} />
                )
              }
              size="xs"
              minW="24px"
              h="24px"
              variant="ghost"
              color="base-white"
              _hover={{ bg: 'ats-bg-secondary' }}
              _active={{ bg: 'ats-bg-secondary' }}
              onClick={(e) => {
                e.stopPropagation();
                handleMinimize();
              }}
            />
          </Tooltip>

          {/* Expand/Fullscreen button */}
          {showExpandButton && (
            <Tooltip
              label="Go to recent activities"
              hasArrow={false}
              placement="top"
            >
              <IconButton
                aria-label="Go to recent activities"
                icon={<Icon as={Expand03} w="14px" h="14px" />}
                size="xs"
                minW="24px"
                h="24px"
                variant="ghost"
                color="base-white"
                _hover={{ bg: 'ats-bg-secondary' }}
                _active={{ bg: 'ats-bg-secondary' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpand();
                }}
              />
            </Tooltip>
          )}

          {/* Close button */}
          {showCloseButton && (
            <Tooltip
              label={inProgressCount > 0 ? 'Minimize' : 'Close'}
              hasArrow={false}
              placement="top"
            >
              <IconButton
                aria-label="Close"
                icon={<XIcon width={14} height={14} />}
                size="xs"
                minW="24px"
                h="24px"
                variant="ghost"
                color="base-white"
                _hover={{ bg: 'ats-bg-secondary' }}
                _active={{ bg: 'ats-bg-secondary' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
              />
            </Tooltip>
          )}
        </Flex>
      </Flex>

      {/* Body - Collapses on minimize like AiScoringBulkProgress */}
      <Box
        bg="white"
        overflow="hidden"
        transition="all 0.15s ease-out"
        maxH={isOpen ? '400px' : '0'}
        opacity={isOpen ? 1 : 0}
      >
        <Box overflowY="auto" maxH="400px">
          {groups.length > 0 ? (
            /* Render all operations using action-specific components */
            <Box>
              {groups.map((group) => {
                // For single operation, render items directly without group header
                if (isSingleOperation) {
                  return (
                    <Box key={group.id}>
                      {group.items.map((item) => {
                        const ActionComponent = getActionComponent(group.id);
                        return (
                          <ActionComponent
                            key={item.id}
                            item={item}
                            activityType={group.id}
                          />
                        );
                      })}
                    </Box>
                  );
                }
                // For multiple operations, use group sections
                return <OperationGroupSection key={group.id} group={group} />;
              })}
            </Box>
          ) : (
            /* Fallback during data transition */
            <Box py="16px" px="20px">
              <KText size="sm" color="secondary-700">
                Processing...
              </KText>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Export the sparkles icon for AI operations
export { HiOutlineSparkles };
